<template>
  <div class="artwork-player-controller">
    <header>
      <div :style="{backgroundImage:`url(${thumbnailUrl})`}" class="thumbnail" v-if="thumbnailUrl"></div>
      <h2>{{ artworkTitle }}</h2>
    </header>
    <div class="row"><p>Display Setup</p></div>
    <div class="display-setups row">
      <div v-for="(config, i) in artworkDisplaySetups"
           :key="i"
           class="display-setup"
           :class="{active:curSetupId===i, online:setupIsOnDisplay(i)}"
           @click="selectSetup(i)">
        <span>{{i+1}}</span>
      </div>
      <div v-if="!curSetupIsSaved" class="display-setup new active">
        <span>{{curSetupId+1}}</span>
      </div>
      <button v-if="canAddDisplaySetup" class="plus" @click="addSetup()">
        <i class="plus"/>
      </button>
    </div>
    <section class="config-selector" v-if="configsNumber > 1">
      <div class="row desc"><span>{{ multiConfigMessage }}</span></div>
      <dropdown-select
        v-if="curDisplaySetup"
        :options="configsOptions"
        v-model="curDisplaySetup.configId"
      />
    </section>
    <section class="channels table" v-if="configChannels">
      <div class="row desc">
        <div>
          <span v-if="multiChannelMessage">{{multiChannelMessage}}</span>
          <span>{{channelMessage}}</span>
        </div>
      </div>
      <template v-if="curDisplaySetup && curDisplaySetup.channelPlayers.length">
        <div :class="{wired:curDisplaySetup.channelPlayers[i]}"
             :key="i" class="row"
             v-for="(channel, i) in configChannels">
          <div class="cell">
            <div class="label round left"><span>{{ i+1 }}</span></div>
            <span>{{ channel.source.toString() }}</span>
          </div>
          <player-select
            :class="{online:playerIsOnline(i)}"
            v-model="curDisplaySetup.channelPlayers[i]"
            :options="availablePlayers"
          />
        </div>
      </template>
    </section>
    <section class="player-bar">
      <button
        v-if="!curConfigIsOnDisplay"
        :disabled="setupNotComplete"
        @click="play"
        class="large round play">
        <i class="play"/>
      </button>
      <button
        v-if="curConfigIsOnDisplay"
        @click="stop"
        class="large round stop">
        <i class="stop"/>
      </button>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { Player } from '../../models/Player'
import { AWChannels } from '../../models/AWChannel'
import DropdownSelect from '../elements/UI/inputs/DropdownSelect'
import PlayerSelect from '../elements/PlayerSelect'

/** @typedef {{
 *  configId: number
 *  channelPlayers: string[]  key is the channel index, value is the player id
 * }} DisplaySetup
 */

export default {
  name: 'DisplaySetupController',
  props: {
    /** @type Artwork */
    artwork: Object
  },
  components: { DropdownSelect, PlayerSelect },

  data () {
    return {
      curSetupId: 0,
      /** @type {DisplaySetup} */
      curDisplaySetup: null,
      /** @type {DisplaySetup[]} */
      localDisplaySetups: []
    }
  },

  computed: {
    ...mapState(['userAccount', 'accountPlayers']),
    ...mapGetters(['accountDisplaySetups']),

    artworkDisplaySetups () {
      if (!this.accountDisplaySetups || !Object.keys(this.accountDisplaySetups).length) {
        return []
      }
      return this.accountDisplaySetups[this.artwork.key] || []
    },

    players () {
      if (this.accountPlayers) {
        return this.accountPlayers.map(player => Player.fromJson(player))
      }
      return []
    },

    availablePlayers () {
      if (!this.curDisplaySetup) return this.players
      const playersInUse = this.curDisplaySetup.channelPlayers.filter(playerId => playerId !== null)
      return this.players.filter(player => playersInUse.indexOf(player.key) < 0)
    },

    artworkOnlinePlayers () {
      return this.players.reduce((players, p) => {
        if (p.connected && p.isArtworkLaunched(this.artwork.key)) players[p.key] = p
        return players
      }, {})
    },

    thumbnailUrl () {
      return this.artwork ? this.artwork.thumbnailUrl() : ''
    },

    artworkTitle () {
      return this.artwork ? this.artwork.title : ''
    },

    configsNumber () {
      return this.artwork && this.artwork.setups ? this.artwork.setups.length : 0
    },

    multiConfigMessage () {
      return this.configsNumber > 1 ? `Select a configuration of the artwork` : ''
    },

    multiChannelMessage () {
      if (!this.configsNumber) {
        return ''
      }
      const config = this.artwork.setups[this.curDisplaySetup.configId]
      if (config.channels.length < 2) {
        return ''
      }
      const context = this.configsNumber > 1 ? 'work configuration' : 'work'
      return `This ${context} has ${config.channels.length} channels.`
    },

    channelMessage () {
      return this.multiChannelMessage
        ? 'Assign a player to each one.'
        : 'Select a player'
    },
    configsOptions () {
      if (!this.artwork || !this.artwork.setups.length) {
        return null
      }
      const options = []
      this.artwork.setups.forEach((setup, i) => { options[i] = setup.title || 'Untitled' })
      return options
    },
    configChannels () {
      if (!this.curDisplaySetup) return null
      const channels = this.artwork.setups[this.curDisplaySetup.configId].channels
      return channels.length ? AWChannels.fromJson(channels) : null
    },

    setupNotComplete () {
      if (!this.curDisplaySetup) return false
      return this.curDisplaySetup.channelPlayers.filter(val => val === null || !this.playerIsOn(val)).length > 0
    },

    canAddDisplaySetup () {
      if (!this.curDisplaySetup) return false
      return this.curSetupId < this.artworkDisplaySetups.length
    },

    curSetupIsSaved () {
      return this.curSetupId < this.artworkDisplaySetups.length
    },

    curConfigIsOnDisplay () {
      return this.setupIsOnDisplay(this.curSetupId)
    }
  },

  methods: {
    ...mapActions(['updateAccountDisplaySetup']),
    play () {
      this.saveSetup()
      this.$emit('play', this.curSetupId, this.curDisplaySetup)
    },

    stop () {
      this.$emit('stop', this.curDisplaySetup)
    },

    saveSetup () {
      this.updateAccountDisplaySetup({
        artworkId: this.artwork.key,
        setupIndex: this.curSetupId,
        setup: this.curDisplaySetup
      })
    },

    selectSetup (index) {
      this.curDisplaySetup = this.getDisplaySetupByIndex(index)
      if (!this.curDisplaySetup) {
        this.curDisplaySetup = this.newDisplaySetup()
        index = 0
      }
      this.curSetupId = index
    },

    addSetup () {
      this.curDisplaySetup = this.newDisplaySetup()
      this.curSetupId = this.artworkDisplaySetups.length
    },

    getFirstOnlineConfigIndex () {
      const onlinePlayers = this.artworkOnlinePlayers
      const playerIds = Object.keys(onlinePlayers)

      if (!playerIds.length) return -1

      const firstPlayerId = playerIds.sort((a, b) => onlinePlayers[a].artwork.configId - onlinePlayers[b].artwork.configId)[0]
      // !!! DEBUG !!!
      console.log('---- firstPlayerId: ', firstPlayerId)
      const index = onlinePlayers[firstPlayerId].artwork.configId
      return typeof index !== 'undefined' ? index : -1
    },

    getDisplaySetupByIndex (setupIndex) {
      if (this.artworkDisplaySetups.length && setupIndex >= 0 && setupIndex < this.artworkDisplaySetups.length) {
        // !!! DEBUG !!!
        console.log(`%c getDisplaySetupByIndex() %c this.artworkDisplaySetups[setupIndex]: `, 'background:#ffbb00;color:#000', 'color:#00aaff', this.artworkDisplaySetups[setupIndex])
        return { ...this.artworkDisplaySetups[setupIndex] }
      }
      return this.newDisplaySetup()
    },

    newDisplaySetup (configId, channelPlayers) {
      if (!this.artwork.setups) return null
      configId = configId || 0
      const setup = this.artwork.setups[configId]
      return {
        configId: configId,
        channelPlayers: channelPlayers || setup.channels.map(() => null)
      }
    },

    playerIsOn (id) {
      const player = this.players.find(p => p.key === id)
      if (player) return player.connected
      return false
    },

    playerIsOnline (id) {
      return Object.keys(this.artworkOnlinePlayers).indexOf(id) >= 0
    },

    setupIsOnDisplay (i) {
      if (i >= this.artworkDisplaySetups.length) return false
      if (!this.artworkDisplaySetups[i]) return false
      const onlinePlayerIds = Object.keys(this.artworkOnlinePlayers)
      if (!onlinePlayerIds.length) return false
      return onlinePlayerIds.indexOf(this.artworkDisplaySetups[i].channelPlayers[0]) >= 0
    }
  },

  watch: {
    artwork () {
      this.curSetupId = Math.max(0, this.getFirstOnlineConfigIndex())
      this.curDisplaySetup = this.getDisplaySetupByIndex(this.curSetupId)
    },

    'curDisplaySetup.configId' (value) {
      const setup = this.artwork.setups[value]
      this.curDisplaySetup.channelPlayers = setup.channels.map((ch, i) => {
        if (i < this.curDisplaySetup.channelPlayers.length) return this.curDisplaySetup.channelPlayers[i]
        return null
      })
    },

    'artworkDisplaySetups.length' () {
      // this.curSetupIsSaved = this.curSetupId < this.artworkDisplaySetups
    },

    'artworkDisplaySetups.channelPlayers' () {
      // const playersString = this.curDisplaySetup.channelPlayers.join()
      // const duplicate = this.artworkDisplaySetups.find(c => {
      //   return c.configId === this.curDisplaySetup.configId && c.channelPlayers.join() === playersString
      // })
      // if (duplicate) return
      // this.curSetupIsSaved = this.curSetupId < this.artworkDisplaySetups
    }
  },

  mounted () {
    if (!this.artwork) return
    this.curSetupId = Math.max(0, this.getFirstOnlineConfigIndex())
    this.curDisplaySetup = this.getDisplaySetupByIndex(this.curSetupId)
  }
}
</script>

<style lang="scss">
  @import "../../scss/vars";
  @import "../../scss/mixins";

  .artwork-player-controller {
    width: 100%;
    height: 100%;
    position: relative;

    header {
      margin-bottom: $basic-padding;
      height: $basic-size;
      font-weight: 400;
      display: flex;
      align-items: center;

      .thumbnail {
        width: $basic-size;
        /*border-radius: 50%;*/
        margin-right: $basic-padding;
        flex-shrink: 0;
      }

      h2 {
        margin: 0;
        white-space: nowrap;
        @include text-ellipsis;
      }
    }

    .display-setups {
      .display-setup {
        position: relative;
        width: $basic-size;
        flex-grow: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: $h2-font-size;
        cursor: pointer;
        @include not-selectable;

        &.active {
          border: 1px solid #fff;
        }

        &.new.active {
          border-style: dashed;
          border-color: rgba(0, 0, 0, 0.2)
        }

        &.online{
          color: #000;
          &:after{
            content: '';
            background: $px-color-green;
            width: $three-quarters;
            height: $three-quarters;
            @include centered;
            border-radius: 50%;
            z-index: -1;
          }
        }
      }
    }

    section {
      // margin-bottom: $basic-padding;

      &.player-bar {
        margin: $basic-size /2 0 0;
      }
    }

    .label.round {
      border-style: dashed;
      border-color: rgba(0, 0, 0, 0.2)
    }

    .wired {
      .label.round {
        border-style: solid;
        border-color: $px-color-green
      }
    }

    .row {
      height: $basic-size;

      .cell {
        overflow: hidden;
        white-space: nowrap;

        &:first-child {
          flex-grow: 1;
        }
      }

      &.desc {
        align-items: flex-end;
        padding: $basic-padding 0;
        height: auto;
        min-height: $basic-size;
      }
    }

    button.round {
      box-shadow: 0 0 0 1px $bg-semi-transparent-color;
      margin: auto;
    }
  }
</style>
